import type { Dispatch, SetStateAction } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Skeleton, Upload } from 'antd';
import type { RcFile } from 'antd/lib/upload';
import axios from 'axios';
import getGatewayUri from '../../store/services/getGatewayUri';
import { getEnvironment, Environment } from '../../layout/utils';

const { Dragger } = Upload;

export type UploadControl = { mode: 'auto' } | { mode: 'manual'; startUpload: boolean };

export enum UploadFolder {
  INITIAL = 'unauthenticated-uploads',
  SIMULATION = 'simulations',
  TNT_STAGE = 'shipium-tnt-management-partner-overrides-upload-stage',
  TNT_PROD = 'shipium-tnt-management-partner-overrides-upload-prod',
  BULK_PROCESSING_RAW_UPLOAD = 'asset-bulk-processing',
}

export enum UploadType {
  BULK_PROCESSING_RAW_UPLOAD = 'asset-bulk-processing',
  INITIAL = 'unauthenticated-uploads',
  SIMULATION = 'simulation-shipment-data',
  TNT = 'shipium-tnt-management',
}
const getUploadFolder = (type: UploadType) => {
  switch (type) {
    case UploadType.INITIAL:
      return UploadFolder.INITIAL;
    case UploadType.SIMULATION:
      return UploadFolder.SIMULATION; //'simulation-shipment-data'
    case UploadType.TNT:
      return getEnvironment() === Environment.STAGING ? UploadFolder.TNT_PROD : UploadFolder.TNT_STAGE; //'shipium-tnt-management'
    default:
      return 'unauthenticated-uploads';
  }
};

interface SuccessParams {
  fileName: string;
  mimeType: string;
  size: number;
  s3Key: string;
  s3Bucket: string;
}

export interface PresignedUploadProps {
  uploadType: UploadType;
  showUpload?: boolean;
  dragger?: boolean;
  multipleFiles?: boolean;
  uploadControl?: UploadControl;
  children: React.ReactNode;
  onSuccess: (result: SuccessParams) => void;
  onFailure: (err: Error) => void;
  validate?: (file: RcFile) => Promise<void>;
  loadingState: [boolean, Dispatch<SetStateAction<boolean>>];
  expiration?: number;
  tags?: Record<string, string>;
}

// TODO augment this map if necessary
const typeToExtensionMap: Record<string, string> = {
  'text/csv': 'csv',
  'application/json': 'json',
  'image/png': 'png',
  'text/plain': 'txt',
  'application/pdf': 'pdf',
};

const MULTI_PART_UPLOAD_THRESHOLD = 100 * 1024 * 1024; // 100MB
const PART_SIZE = 50 * 1024 * 1024; // 50MB; 5MB is min allowed size

export const PresignedUpload = ({
  dragger,
  multipleFiles = false,
  uploadControl = { mode: 'auto' },
  uploadType,
  showUpload = false,
  expiration = 100,
  onSuccess,
  onFailure,
  validate,
  loadingState,
  tags,
  ...restProps
}: PresignedUploadProps) => {
  const uploadInitiatedRef = useRef(false);
  const [loading, setLoading] = loadingState;
  const [files, setFiles] = useState<RcFile[]>([]);
  const uploadFolderName = getUploadFolder(uploadType);

  // intercept file selection and store the files instead of uploading
  const beforeUpload = (file: RcFile, fileList: RcFile[]) => {
    if (uploadControl.mode === 'auto') {
      // auto upload => proceed with the upload immediately
      handleUpload(file);
    } else {
      // manual upload => store file if not duplicate
      setFiles((currentFiles) => {
        const isDuplicate = currentFiles.some((cf) => cf.name === file.name && cf.size === file.size);

        if (!isDuplicate) {
          return multipleFiles ? [...currentFiles, file] : [file];
        }
        // no change if duplicate
        return currentFiles;
      });
    }
    // prevent default upload
    return false;
  };

  const handleUpload = useCallback(
    async (fileToUpload: RcFile) => {
      try {
        setLoading(true);
        if (validate) {
          await validate(fileToUpload);
        }

        if (fileToUpload.size < MULTI_PART_UPLOAD_THRESHOLD) {
          //axios expects any type
          const data: any = {
            folder: uploadFolderName,
            fileType: typeToExtensionMap[fileToUpload.type] ?? 'txt',
            fileName: fileToUpload.name,
            uploadType,
            mimeType: fileToUpload.type,
            expiration,
          };

          //add our tags to the data
          if (tags) {
            for (const key in tags) {
              data[key] = tags[key];
            }
          }

          const res = await axios({
            url: `${getGatewayUri()}/getPresignedUploadUrl`,
            method: 'POST',
            data,
            withCredentials: true,
          });
          //axios expects any type
          const headers = {
            'Content-Type': fileToUpload.type,
          };

          await axios({
            url: res.data.uploadUrl,
            method: 'PUT',
            headers,
            data: await fileToUpload.arrayBuffer(),
          });

          onSuccess({
            s3Key: res.data.s3Key,
            s3Bucket: res.data.s3Bucket,
            fileName: fileToUpload.name,
            mimeType: fileToUpload.type,
            size: fileToUpload.size,
          });
        } else {
          const {
            data: { uploadId, key, fileName, bucket },
          } = await axios.post(
            `${getGatewayUri()}/initiateMultipartUpload`,
            {
              fileName: fileToUpload.name,
              mimeType: fileToUpload.type,
              uploadType,
              folder: uploadFolderName,
              uploaderName: tags?.uploaderName || '',
              description: tags?.description || '',
              uploaderEmail: tags?.uploaderEmail || '',
            },
            { withCredentials: true },
          );
          const parts: { ETag: string; PartNumber: number }[] = [];
          const partPromises = [];
          for (let start = 0; start < fileToUpload.size; start += PART_SIZE) {
            const partNumber = Math.floor(start / PART_SIZE) + 1;
            const blob = fileToUpload.slice(start, start + PART_SIZE);
            partPromises.push(
              (async () => {
                const {
                  data: { uploadUrl },
                } = await axios.post(
                  `${getGatewayUri()}/uploadFilePart`,
                  {
                    mimeType: fileToUpload.type,
                    folder: uploadFolderName,
                  },
                  {
                    withCredentials: true,
                    params: { uploadId, partNumber, fileName },
                  },
                );

                const { headers } = await axios.put(uploadUrl, blob);
                parts.push({ ETag: headers.etag, PartNumber: partNumber });
              })(),
            );
          }

          await Promise.all(partPromises);
          await axios.post(
            `${getGatewayUri()}/completeMultipartUpload`,
            {
              //parts must be in order, despite the async nature of the upload
              parts: parts.sort((a: any, b: any) => a.PartNumber - b.PartNumber),
              key,
              uploadId,
              uploadType,
              fileName: fileToUpload.name,
              mimeType: fileToUpload.type,
            },
            { withCredentials: true },
          );
          onSuccess({
            s3Key: key,
            s3Bucket: bucket,
            fileName,
            mimeType: fileToUpload.type,
            size: fileToUpload.size,
          });
        }

        //reset component
        setFiles([]);
      } catch (err) {
        onFailure(err);
      } finally {
        setLoading(false);
      }
    },
    [expiration, onFailure, onSuccess, setLoading, validate, uploadFolderName, uploadType, tags],
  );

  useEffect(() => {
    if (uploadControl.mode === 'manual' && uploadControl.startUpload) {
      // has upload already been initiated?
      if (!uploadInitiatedRef.current) {
        uploadInitiatedRef.current = true; // prevent further uploads
        files.forEach((file) => handleUpload(file));
      }
    } else {
      // allow re-triggering uploads if conditions change
      uploadInitiatedRef.current = false;
    }
  }, [files, handleUpload, uploadControl]);

  return (
    <Skeleton loading={loading}>
      {dragger ? (
        <Dragger
          {...restProps}
          showUploadList={showUpload}
          multiple={multipleFiles}
          beforeUpload={beforeUpload}
          onRemove={() => {
            setFiles([]);
          }}
        >
          {restProps.children}
        </Dragger>
      ) : (
        <Upload {...restProps} showUploadList={showUpload} multiple={multipleFiles} beforeUpload={beforeUpload}>
          {restProps.children}
        </Upload>
      )}
    </Skeleton>
  );
};
